/**
 * asistentesDelActa — qué tabla del acta son los asistentes y qué es cada columna.
 *
 * Mismo problema que con las tareas y misma solución: un acta trae varias
 * tablas y hay que reconocer la correcta por lo que dicen sus cabeceras, no por
 * su posición. La de asistentes es inconfundible por una razón — es la única
 * que tiene una columna de iniciales o de asistencia— pero el rótulo lo escribió
 * quien hizo la plantilla, así que se reconoce por familias de palabras.
 *
 * Por qué importa acertar: sobre esta tabla se hacen cosas que en otra serían
 * un destrozo. El número se pone solo, las iniciales se calculan del nombre, y
 * el puesto se trae del directorio. Hacer eso sobre la tabla de acuerdos
 * sobrescribiría los acuerdos.
 *
 * Funciones puras a propósito: se prueban con las cabeceras reales de las
 * plantillas en vez de a ojo sobre la pantalla.
 */

const ES_ASISTENTES = /(asistente|participante|convocad|integrante|concurrente)/i;

const ES_NUMERO = /^\s*(no\.?|n[úu]m(?:ero)?\.?|#|id|consecutivo)\s*$/i;
const ES_NOMBRE = /(nombre|asistente|participante|persona)/i;
const ES_INICIALES = /(iniciales|siglas|abreviatura)/i;
const ES_PUESTO = /(puesto|cargo|rol|posici[oó]n|[áa]rea|departamento|title)/i;
// Anclado a los extremos, al contrario que los demas: «Firma» a secas y nada
// mas. Suelto se llevaria por delante «Firmado por» o «Firma del responsable»
// de otra tabla, y ahi no va ninguna rubrica. Es la misma regla que usa el
// servidor al estampar (`columnasDeFirma`, en FirmaEnDocx): si las dos no
// dijeran lo mismo, la palomita saldria en una columna y la firma en otra.
const ES_FIRMA = /^\s*firmas?\s*$/i;
const ES_ASISTENCIA = /(asistencia|puntualidad)/i;

/**
 * Los estados que la propia cabecera enumera.
 *
 * «(P) Puntual(R) Retardo(N) No asistió(PSI) Para su información» no es un
 * título: es una leyenda, y dice exactamente qué se puede poner en esa columna.
 * Leerla de ahí y no de una lista escrita en el código es lo que hace que esto
 * sirva para la plantilla de cualquiera — otra empresa usa «(A) Asistió», y una
 * lista fija le pondría en el acta un código que su propia leyenda no explica.
 *
 * Hacen falta dos como mínimo. Con uno solo, cualquier cabecera con un
 * paréntesis —«Puesto (opcional)»— pasaría por lista de estados.
 *
 * @returns {Array<{codigo:string, texto:string, valor:string}>}
 */
export function estadosDeAsistencia(cabecera) {
  const texto = String(cabecera || '');
  const estados = [];
  const vistos = new Set();

  const patron = /\(([^()]{1,8})\)\s*([^(]*)/g;
  let hallazgo = patron.exec(texto);
  while (hallazgo !== null) {
    const codigo = hallazgo[1].trim();
    const clave = codigo.toLowerCase();
    if (codigo && !vistos.has(clave)) {
      vistos.add(clave);
      estados.push({ codigo, texto: hallazgo[2].trim(), valor: `(${codigo})` });
    }
    hallazgo = patron.exec(texto);
  }

  return estados.length >= 2 ? estados : [];
}

/**
 * Cómo se llama esa columna cuando la leyenda ya no hace falta leerla.
 *
 * En el Word la leyenda se queda: ahí es lo único que explica los códigos a
 * quien recibe el acta. En la pantalla no, porque los estados están dentro del
 * desplegable, y una cabecera de setenta caracteres ensancha la columna hasta
 * echar fuera a las demás.
 */
export function rotuloDeAsistencia(cabecera) {
  const texto = String(cabecera || '').trim();
  const antes = texto.split('(')[0].trim();
  return antes || 'Asistencia';
}

/** La columna de la leyenda, o la que se llame «Asistencia» a secas. */
function columnaDeAsistencia(columnas, tomadas) {
  for (let i = 0; i < columnas.length; i++) {
    if (tomadas.has(i)) continue;
    const texto = String(columnas[i] || '');
    if (estadosDeAsistencia(texto).length > 0 || ES_ASISTENCIA.test(texto)) return i;
  }
  return -1;
}

/** El primer índice que cumpla, saltándose los ya asignados. */
function columnaQue(columnas, patron, tomadas) {
  for (let i = 0; i < columnas.length; i++) {
    if (tomadas.has(i)) continue;
    if (patron.test(String(columnas[i] || ''))) return i;
  }
  return -1;
}

/**
 * Qué significa cada columna, si esta tabla es de asistentes.
 *
 * La de firma puede no estar: muchas plantillas no la llevan, y su ausencia no
 * descalifica a la tabla. Cuando esta, es donde se pone la palomita de quien
 * ya firmo y donde el servidor estampa la rubrica dibujada.
 *
 * Devuelve `null` cuando no lo es. Hace falta una columna de nombre —sin ella
 * no hay a quién apuntar— y además algo que solo tienen los asistentes:
 * iniciales o puesto. Con el nombre a secas, la tabla de acuerdos con una
 * columna «Responsable» pasaría por lista de asistentes.
 */
export function columnasDeAsistentes(columnas) {
  if (!Array.isArray(columnas) || columnas.length < 2) return null;

  const tomadas = new Set();
  const asignar = (patron) => {
    const i = columnaQue(columnas, patron, tomadas);
    if (i >= 0) tomadas.add(i);
    return i;
  };

  const numero = asignar(ES_NUMERO);
  // La asistencia, de las primeras: su cabecera es la más reconocible de todas
  // —lleva la leyenda dentro— y así no se la lleva ninguna de las otras.
  const asistencia = columnaDeAsistencia(columnas, tomadas);
  if (asistencia >= 0) tomadas.add(asistencia);
  const iniciales = asignar(ES_INICIALES);
  const puesto = asignar(ES_PUESTO);
  // El nombre al final: es la columna que peor se reconoce por su título, y así
  // no se lleva por delante a «Iniciales» ni a «Puesto / Rol».
  const nombre = asignar(ES_NOMBRE);
  // Despues del nombre: ES_NOMBRE no la reclamaria, pero el orden importa aqui
  // y dejarlo claro cuesta una linea.
  const firma = asignar(ES_FIRMA);

  if (nombre < 0) return null;
  if (iniciales < 0 && puesto < 0) return null;

  return { numero, nombre, iniciales, puesto, asistencia, firma };
}

/**
 * La zona de asistentes del acta, si la hay.
 *
 * Se mira primero el rótulo y después las cabeceras. Al revés daría con
 * cualquier tabla que tenga nombre y puesto —un directorio de contactos
 * anexado, por ejemplo— antes que con la que lo dice en su título.
 */
export function zonaDeAsistentes(regiones) {
  const listas = (regiones || []).filter(r => r && r.tipo === 'lista');

  const porRotulo = listas.find(
    r => ES_ASISTENTES.test(String(r.etiqueta || '')) && columnasDeAsistentes(r.columnas)
  );
  if (porRotulo) return { region: porRotulo, columnas: columnasDeAsistentes(porRotulo.columnas) };

  const porCabeceras = listas.find(r => columnasDeAsistentes(r.columnas));
  if (porCabeceras) {
    return { region: porCabeceras, columnas: columnasDeAsistentes(porCabeceras.columnas) };
  }

  return null;
}

/**
 * El puesto que se puede dar por bueno para un asistente.
 *
 * Recibe a quienes el directorio propone para un nombre —uno, o varios cuando
 * el nombre no los distingue— y devuelve el cargo SOLO si no hay nada que
 * elegir. Si no, cadena vacía.
 *
 * Por qué no se toma el primero: en un directorio real hay cinco cuentas que se
 * llaman «Jorge Iván Espadas Espinosa», unas con «CEO», otras con «Director
 * General» y otras sin nada. Escoger una es inventarse el cargo de alguien en
 * un documento que se firma, y además hacerlo en silencio, donde nadie lo va a
 * revisar. Ante la duda se deja en blanco: un hueco es una pregunta visible, y
 * quien abra el acta puede elegir a la persona exacta en el desplegable, que
 * ahí sí las distingue por su correo.
 *
 * Las cuentas sin cargo no cuentan como discrepancia. No dicen nada, y un
 * duplicado vacío no debería tapar lo que la otra ficha sí dice.
 *
 * La comparación ignora tildes y mayúsculas: «Director General» y «Director
 * general» son el mismo cargo escrito por dos manos distintas, y tratarlos como
 * un desacuerdo dejaría el hueco por una diferencia que nadie ve.
 */
export function puestoDeCandidatos(candidatos) {
  if (!Array.isArray(candidatos)) return '';

  const distintos = new Map();
  for (const persona of candidatos) {
    const puesto = String((persona && persona.puesto) || '').trim();
    if (!puesto) continue;
    const clave = puesto
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/\s+/g, ' ');
    if (!distintos.has(clave)) distintos.set(clave, puesto);
  }

  return distintos.size === 1 ? distintos.values().next().value : '';
}

// Lo que no cuenta para las iniciales. «Juan de la Cruz» es JC, no JDLC.
const PARTICULAS = new Set([
  'de', 'del', 'la', 'las', 'los', 'y', 'e', 'da', 'do', 'dos', 'van', 'von', 'di', 'san'
]);

/**
 * Las iniciales de un nombre: la primera letra de cada parte.
 *
 * «Iván Aurelio Patiño Barragán» → IAPB. Sin tildes, porque unas iniciales con
 * tilde no son iniciales, y en mayúsculas porque es lo que son.
 *
 * Se calculan y no se escriben. Tecleadas a mano salían mal en cuanto alguien
 * corregía un apellido: el nombre cambiaba y las iniciales se quedaban.
 */
export function inicialesDe(nombre) {
  return String(nombre || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\s'-]/gu, ' ')
    .split(/[\s'-]+/)
    .filter(p => p && !PARTICULAS.has(p.toLowerCase()))
    .map(p => p[0].toUpperCase())
    .join('');
}

const asistentesDelActa = {
  columnasDeAsistentes, zonaDeAsistentes, inicialesDe, puestoDeCandidatos,
  estadosDeAsistencia, rotuloDeAsistencia
};
export default asistentesDelActa;
